import { incomeRangeOptions } from './07-employment.js';
import type { FieldGroup } from './types.js';

export const familyMemberOptions = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'step_father', label: 'Step-father' },
  { value: 'step_mother', label: 'Step-mother' },
  { value: 'wife_husband', label: 'Wife / Husband' },
  { value: 'blood_brother', label: 'Blood Brother' },
  { value: 'blood_sister', label: 'Blood Sister' },
  { value: 'half_brother', label: 'Half Brother' },
  { value: 'half_sister', label: 'Half Sister' },
  { value: 'step_brother', label: 'Step Brother' },
  { value: 'step_sister', label: 'Step Sister' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'other', label: 'Other' },
] as const;

export const digitalAccessOptions = [
  { value: 'smartphone', label: 'Smartphone' },
  { value: 'feature_phone', label: 'Feature phone' },
  { value: 'computer_laptop', label: 'Computer / Laptop' },
  { value: 'internet_at_home', label: 'Internet at home' },
  { value: 'no_internet', label: 'No internet access' },
  { value: 'no_phone', label: 'No phone' },
] as const;

/**
 * Household income — uses the SAME bucket set as personal `incomeRange`
 * (step 7) so the two questions feel consistent. The 9-bucket version
 * from step 7 is more granular at the high end (adds `2_5L_5L` between
 * `1L_2_5L` and `above_5L`) than the original 8-bucket household list,
 * which capped at "above ₹2.5 Lakh". Re-exporting (rather than declaring
 * a parallel array) guarantees the two stay in lockstep — if either side
 * ever needs different buckets, that's a deliberate decision the
 * re-exporter must make explicitly.
 */
export const householdIncomeOptions = incomeRangeOptions;

export const maritalStatusOptions = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const housingTypeOptions = [
  { value: 'own_house', label: 'Own house' },
  { value: 'rented', label: 'Rented' },
  { value: 'government_quarters', label: 'Government quarters' },
  { value: 'shared', label: 'Shared housing' },
  { value: 'homeless', label: 'Homeless' },
  { value: 'other', label: 'Other' },
] as const;

export const bplCardOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'applied', label: 'Applied / Pending' },
  { value: 'dont_know', label: "Don't know" },
] as const;

export const migrationStatusOptions = [
  { value: 'native', label: 'Native (always lived here)' },
  { value: 'within_district', label: 'Migrated within district' },
  { value: 'within_state', label: 'Migrated within state' },
  { value: 'inter_state', label: 'Inter-state migrant' },
  { value: 'international', label: 'International migrant' },
] as const;

export const landOwnershipOptions = [
  { value: 'own_agricultural', label: 'Own agricultural land' },
  { value: 'own_residential', label: 'Own residential land' },
  { value: 'government_allotted', label: 'Government allotted' },
  { value: 'no_land', label: 'No land' },
  { value: 'leased', label: 'Leased land' },
  { value: 'other', label: 'Other' },
] as const;

export const primaryIncomeSourceOptions = [
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'government_job', label: 'Government job' },
  { value: 'private_job', label: 'Private job' },
  { value: 'business', label: 'Business' },
  { value: 'daily_wage', label: 'Daily wage' },
  { value: 'pension', label: 'Pension' },
  { value: 'remittance', label: 'Remittance' },
  { value: 'no_fixed_income', label: 'No fixed income' },
  { value: 'other', label: 'Other' },
] as const;

export const household: FieldGroup = {
  key: 'household',
  label: 'Household & Economic Profile',
  step: 12,
  description: 'Family, housing, income, and economic profile.',
  fields: [
    {
      order: 1,
      key: 'maritalStatus',
      label: 'Marital Status',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...maritalStatusOptions],
    },
    {
      order: 2,
      key: 'familyMembers',
      label: 'Family Members',
      type: 'multi_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...familyMemberOptions],
    },
    // ── Family-link sub-steps (orders 3–16) ─────────────────────────────────
    // Each is conditional on the user having selected the matching value in
    // `familyMembers` (sub-step 2). Female-first ordering: mother → step-mother
    // → father → step-father → spouse → sisters → brothers → daughter → son
    // → other. Single-instance relations (mother / father / step_*) are
    // typed `object`; multi-instance (siblings / children / spouse / other)
    // are typed `object_array`.
    {
      order: 3,
      key: 'linkedMother',
      label: 'Link your mother',
      type: 'object',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['mother'] },
    },
    {
      order: 4,
      key: 'linkedStepMother',
      label: 'Link your step-mother',
      type: 'object',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['step_mother'] },
    },
    {
      order: 5,
      key: 'linkedFather',
      label: 'Link your father',
      type: 'object',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['father'] },
    },
    {
      order: 6,
      key: 'linkedStepFather',
      label: 'Link your step-father',
      type: 'object',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['step_father'] },
    },
    {
      order: 7,
      key: 'linkedSpouses',
      label: 'Link your spouse',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['wife_husband'] },
    },
    {
      order: 8,
      key: 'linkedBloodSisters',
      label: 'Link your blood sisters',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['blood_sister'] },
    },
    {
      order: 9,
      key: 'linkedHalfSisters',
      label: 'Link your half-sisters',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['half_sister'] },
    },
    {
      order: 10,
      key: 'linkedStepSisters',
      label: 'Link your step-sisters',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['step_sister'] },
    },
    {
      order: 11,
      key: 'linkedBloodBrothers',
      label: 'Link your blood brothers',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['blood_brother'] },
    },
    {
      order: 12,
      key: 'linkedHalfBrothers',
      label: 'Link your half-brothers',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['half_brother'] },
    },
    {
      order: 13,
      key: 'linkedStepBrothers',
      label: 'Link your step-brothers',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['step_brother'] },
    },
    {
      order: 14,
      key: 'linkedDaughters',
      label: 'Link your daughters',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['daughter'] },
    },
    {
      order: 15,
      key: 'linkedSons',
      label: 'Link your sons',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['son'] },
    },
    {
      order: 16,
      key: 'linkedOthers',
      label: 'Link other family',
      type: 'object_array',
      step: 12,
      isOnboarding: true,
      optional: true,
      conditionalOn: { field: 'familyMembers', includesAny: ['other'] },
    },
    {
      order: 17,
      key: 'numberOfDependents',
      label: 'Number of Dependents',
      type: 'number',
      step: 12,
      isOnboarding: true,
      optional: true,
      min: 0,
      max: 50,
    },
    {
      order: 18,
      key: 'housingType',
      label: 'Housing Type',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...housingTypeOptions],
    },
    {
      order: 19,
      key: 'householdIncome',
      label: 'Household Income',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...householdIncomeOptions],
      sensitive: true,
    },
    {
      order: 20,
      key: 'bplCard',
      label: 'BPL Card',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...bplCardOptions],
    },
    {
      order: 21,
      key: 'digitalAccess',
      label: 'Digital Access',
      type: 'multi_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...digitalAccessOptions],
    },
    {
      order: 22,
      key: 'migrationStatus',
      label: 'Migration Status',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...migrationStatusOptions],
    },
    {
      order: 23,
      key: 'landOwnership',
      label: 'Land Ownership',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...landOwnershipOptions],
    },
    {
      order: 24,
      key: 'primaryIncomeSource',
      label: 'Primary Income Source',
      type: 'single_select',
      step: 12,
      isOnboarding: true,
      optional: true,
      options: [...primaryIncomeSourceOptions],
    },
  ],
};
